import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { apiATC } from '../../config';
import {
  Paper, Typography, Button, Select, MenuItem, FormControl,
  Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Snackbar, CircularProgress, Box
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import SearchIcon from '@material-ui/icons/Search';
import RefreshIcon from '@material-ui/icons/Refresh';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '@material-ui/icons/Close';
import Alert from '@material-ui/lab/Alert';

const API_URL = `${apiATC}/tools/clean_db.php`;

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto',
    fontFamily: '"Google Sans", Roboto, Arial, sans-serif',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 400,
    fontSize: '1.5rem',
    color: '#202124',
  },
  subtitle: {
    color: '#5f6368',
    fontSize: '0.875rem',
    marginBottom: theme.spacing(3),
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  card: {
    borderRadius: 8,
    border: '1px solid #dadce0',
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: '0 1px 3px 0 rgba(60,64,67,.3), 0 4px 8px 3px rgba(60,64,67,.15)',
    },
  },
  cardHeader: {
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dadce0',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  groupLabel: {
    fontSize: '0.8rem',
    color: '#5f6368',
    fontWeight: 500,
  },
  groupDim: {
    fontSize: '0.8rem',
    color: '#1a73e8',
    fontWeight: 500,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2),
    borderBottom: '1px solid #f1f3f4',
    gap: theme.spacing(2),
    transition: 'background 0.15s',
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      backgroundColor: '#f8f9fa',
    },
  },
  idBadge: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#202124',
    backgroundColor: '#e8eaed',
    borderRadius: 4,
    padding: '2px 8px',
    minWidth: 50,
    textAlign: 'center',
  },
  dims: {
    fontFamily: '"Roboto Mono", monospace',
    fontSize: '0.85rem',
    color: '#3c4043',
    minWidth: 140,
  },
  usageBadges: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    minWidth: 200,
  },
  badgeUsed: {
    fontSize: '0.75rem',
    height: 24,
    backgroundColor: '#e8f0fe',
    color: '#1967d2',
    fontWeight: 500,
  },
  badgeUnused: {
    fontSize: '0.75rem',
    height: 24,
    backgroundColor: '#f1f3f4',
    color: '#80868b',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  replaceSelect: {
    minWidth: 100,
    fontSize: '0.85rem',
    '& .MuiSelect-select': {
      padding: '6px 10px',
    },
  },
  deleteBtn: {
    color: '#d93025',
    border: '1px solid #d93025',
    borderRadius: 20,
    padding: '4px 16px',
    fontSize: '0.8rem',
    fontWeight: 500,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#fce8e6',
    },
    '&:disabled': {
      color: '#dadce0',
      borderColor: '#dadce0',
    },
  },
  flexGrow: {
    flex: 1,
  },
  searchChip: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#d2e3fc',
    },
  },
  emptyUsage: {
    color: '#80868b',
    fontSize: '0.8rem',
    fontStyle: 'italic',
  },
  confirmDeleteId: {
    color: '#d93025',
    fontWeight: 600,
  },
  confirmKeepId: {
    color: '#1967d2',
    fontWeight: 600,
  },
  clickableBadge: {
    fontSize: '0.75rem',
    height: 24,
    cursor: 'pointer',
    '&:hover': {
      filter: 'brightness(0.92)',
    },
  },
  centreDetailRow: {
    display: 'flex',
    gap: 12,
    padding: '6px 0',
    borderBottom: '1px solid #f1f3f4',
    fontSize: '0.85rem',
    '&:last-child': { borderBottom: 'none' },
  },
  centreLabel: {
    color: '#5f6368',
    minWidth: 90,
    fontWeight: 500,
  },
}));

// Regroupe les doublons par (type_pier, forme, color) pour les afficher ensemble
function groupDoublons(doublons) {
  const groups = {};
  doublons.forEach((row) => {
    const key = `${row.type_pier}_${row.forme}_${row.color}`;
    if (!groups[key]) {
      groups[key] = {
        type_label: row.type_label,
        forme_label: row.forme_label,
        couleur_label: row.couleur_label,
        items: [],
      };
    }
    groups[key].items.push(row);
  });
  return Object.values(groups);
}

function DoublonPierre() {
  const classes = useStyles();
  const [doublons, setDoublons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [utilisation, setUtilisation] = useState({});
  const [loadingUtil, setLoadingUtil] = useState({});
  const [replaceWith, setReplaceWith] = useState({}); // { [id_pier]: id_remplacement }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // { old_id, new_id }
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [replacing, setReplacing] = useState(false);
  const [centreDetail, setCentreDetail] = useState({ open: false, id_pier: null, data: [], loading: false });

  const fetchDoublons = useCallback(async () => {
    setLoading(true);
    setUtilisation({});
    setReplaceWith({});
    try {
      const res = await axios.get(`${API_URL}?action=doublons`);
      setDoublons(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setSnackbar({ open: true, message: 'Erreur lors du chargement des doublons', severity: 'error' });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchDoublons();
  }, [fetchDoublons]);

  const checkUtilisation = async (id_pier) => {
    setLoadingUtil((prev) => ({ ...prev, [id_pier]: true }));
    try {
      const res = await axios.get(`${API_URL}?action=utilisee&id_pierre=${id_pier}`);
      setUtilisation((prev) => ({ ...prev, [id_pier]: res.data }));
    } catch (e) {
      setSnackbar({ open: true, message: `Erreur vérification pierre ${id_pier}`, severity: 'error' });
    }
    setLoadingUtil((prev) => ({ ...prev, [id_pier]: false }));
  };

  const checkAllUtilisations = async () => {
    for (const row of doublons) {
      await checkUtilisation(row.id_pier);
    }
  };

  const openCentreDetail = async (id_pier) => {
    setCentreDetail({ open: true, id_pier, data: [], loading: true });
    try {
      const res = await axios.get(`${API_URL}?action=detail_centres&id_pierre=${id_pier}`);
      setCentreDetail((prev) => ({ ...prev, data: Array.isArray(res.data) ? res.data : [], loading: false }));
    } catch (e) {
      setCentreDetail((prev) => ({ ...prev, loading: false }));
      setSnackbar({ open: true, message: `Erreur chargement détails centres pierre ${id_pier}`, severity: 'error' });
    }
  };

  const openConfirm = (old_id, new_id) => {
    setPendingAction({ old_id, new_id });
    setConfirmOpen(true);
  };

  const handleRemplacer = async () => {
    if (!pendingAction) return;
    setConfirmOpen(false);
    setReplacing(true);
    try {
      await axios.post(`${API_URL}?action=remplacer`, {
        old_id: pendingAction.old_id,
        new_id: pendingAction.new_id,
      });
      await axios.post(`${API_URL}?action=supprimer`, {
        id_pierre: pendingAction.old_id,
      });
      setSnackbar({
        open: true,
        message: `Pierre ${pendingAction.old_id} → ${pendingAction.new_id} : remplacée et supprimée`,
        severity: 'success',
      });
      fetchDoublons();
    } catch (e) {
      setSnackbar({ open: true, message: 'Erreur lors du remplacement', severity: 'error' });
    }
    setReplacing(false);
    setPendingAction(null);
  };

  const renderUsageBadges = (id_pier) => {
    const util = utilisation[id_pier];
    if (!util) return <span className={classes.emptyUsage}>Non vérifié</span>;
    return (
      <>
        <Chip size="small" label={`Tarifs: ${util.tarifs || 0}`}
          className={util.tarifs > 0 ? classes.badgeUsed : classes.badgeUnused} />
        <Chip size="small" label={`Centres: ${util.centre_pierres || 0}`}
          className={util.centre_pierres > 0 ? `${classes.badgeUsed} ${classes.clickableBadge}` : classes.badgeUnused}
          onClick={util.centre_pierres > 0 ? () => openCentreDetail(id_pier) : undefined} />
        <Chip size="small" label={`Reçues: ${util.pierres_recues || 0}`}
          className={util.pierres_recues > 0 ? classes.badgeUsed : classes.badgeUnused} />
      </>
    );
  };

  const groups = groupDoublons(doublons);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.title}>Doublons de pierres</Typography>
          <Typography className={classes.subtitle}>
            {doublons.length} pierre{doublons.length > 1 ? 's' : ''} en doublon
            {' · '}{groups.length} groupe{groups.length > 1 ? 's' : ''}
          </Typography>
        </div>
        <div className={classes.toolbar}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<SearchIcon />}
            onClick={checkAllUtilisations}
            style={{ textTransform: 'none', borderRadius: 20 }}
          >
            Tout vérifier
          </Button>
          <Tooltip title="Rafraîchir">
            <IconButton onClick={fetchDoublons} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      ) : doublons.length === 0 ? (
        <Alert severity="info" style={{ borderRadius: 8 }}>Aucun doublon trouvé.</Alert>
      ) : (
        groups.map((group, gi) => (
          <Paper key={gi} className={classes.card} elevation={0}>
            <div className={classes.cardHeader}>
              <span className={classes.groupLabel}>
                {group.type_label} · {group.forme_label} · {group.couleur_label}
              </span>
              <span className={classes.groupDim}>{group.items.length} pierres</span>
            </div>
            {group.items.map((row) => {
              const susceptibles = group.items.filter((r) =>
                r.id_pier !== row.id_pier && (r.dim1 === row.dim1 || r.dim2 === row.dim2)
              );
              const selectedReplace = replaceWith[row.id_pier] || (susceptibles.length > 0 ? susceptibles[0].id_pier : '');
              return (
                <div key={row.id_pier} className={classes.row}>
                  <span className={classes.idBadge}>{row.id_pier}</span>
                  <span className={classes.dims}>
                    {row.dim1} × {row.dim2}{row.dim3 ? ` × ${row.dim3}` : ''}
                  </span>
                  <div className={classes.usageBadges}>
                    {renderUsageBadges(row.id_pier)}
                  </div>
                  {!utilisation[row.id_pier] && (
                    <Tooltip title="Vérifier l'utilisation">
                      <IconButton size="small" onClick={() => checkUtilisation(row.id_pier)}
                        disabled={loadingUtil[row.id_pier]}>
                        {loadingUtil[row.id_pier] ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                  <div className={classes.flexGrow} />
                  <div className={classes.actions}>
                    <Typography variant="caption" color="textSecondary" style={{ marginRight: 4 }}>
                      Remplacer par
                    </Typography>
                    <FormControl size="small" variant="outlined">
                      <Select
                        className={classes.replaceSelect}
                        value={selectedReplace}
                        onChange={(e) => setReplaceWith((prev) => ({ ...prev, [row.id_pier]: e.target.value }))}
                        displayEmpty
                      >
                        <MenuItem value="" disabled><em>ID…</em></MenuItem>
                        {susceptibles.map((o) => (
                          <MenuItem key={o.id_pier} value={o.id_pier}>
                            {o.id_pier} ({o.dim1}×{o.dim2})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <ArrowForwardIcon style={{ color: '#80868b', fontSize: 18 }} />
                    <Button
                      className={classes.deleteBtn}
                      size="small"
                      variant="outlined"
                      startIcon={<DeleteOutlineIcon style={{ fontSize: 16 }} />}
                      disabled={!selectedReplace || replacing}
                      onClick={() => openConfirm(row.id_pier, selectedReplace)}
                    >
                      Supprimer
                    </Button>
                  </div>
                </div>
              );
            })}
          </Paper>
        ))
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}
        PaperProps={{ style: { borderRadius: 12, maxWidth: 440 } }}>
        <DialogTitle style={{ paddingBottom: 8 }}>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Pierre <span className={classes.confirmDeleteId}>{pendingAction?.old_id}</span> sera
            remplacée par <span className={classes.confirmKeepId}>{pendingAction?.new_id}</span> dans
            les tables tarifs et pierres_recues, puis supprimée.
            <br /><br />
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '8px 24px 16px' }}>
          <Button onClick={() => setConfirmOpen(false)}
            style={{ textTransform: 'none', borderRadius: 20 }}>
            Annuler
          </Button>
          <Button onClick={handleRemplacer} variant="contained"
            style={{ textTransform: 'none', borderRadius: 20, backgroundColor: '#d93025', color: '#fff' }}>
            {replacing ? <CircularProgress size={18} style={{ color: '#fff' }} /> : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={centreDetail.open} onClose={() => setCentreDetail({ ...centreDetail, open: false })}
        PaperProps={{ style: { borderRadius: 12, minWidth: 400, maxWidth: 560 } }}>
        <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8 }}>
          Centres utilisant la pierre {centreDetail.id_pier}
          <IconButton size="small" onClick={() => setCentreDetail({ ...centreDetail, open: false })}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {centreDetail.loading ? (
            <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>
          ) : centreDetail.data.length === 0 ? (
            <Typography variant="body2" color="textSecondary">Aucun centre trouvé.</Typography>
          ) : (
            centreDetail.data.map((c, i) => (
              <div key={i} className={classes.centreDetailRow}>
                <span style={{ fontWeight: 500, color: '#202124', minWidth: 50 }}>{c.ref_cv}</span>
                <span style={{ color: '#5f6368' }}>—</span>
                <span>{c.ref_centre}</span>
              </div>
            ))
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity} variant="filled" style={{ borderRadius: 8 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default DoublonPierre;
